const _excluded = ["children", "size", "variant", "type", "disabled", "loading", "fullWidth", "startIcon", "endIcon", "onClick", "contentColor", "overrideClassName"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import * as React from "react";
import { SpinnerIcon } from "../SpinnerIcon";
import { cx } from "../../helpers/utils/cx";
const NOOP = () => {};
const colors = ["text-primary", "text-secondary", "text-tertiary", "text-action-primary", "text-action-secondary", "text-danger-primary", "text-danger-secondary", "text-warning-primary", "text-warning-secondary", "text-positive-primary", "text-positive-secondary", "text-promo-primary", "text-promo-secondary", "text-link-primary", "text-link-secondary", "text-primary-inverted"];
const sizes = {
  sm: "px-4 py-0 h-9 gap-1 bolt-font-body-s-accent",
  md: "px-6 py-0 h-12 gap-2 bolt-font-body-m-accent",
  lg: "px-6 py-0 h-14 gap-2 bolt-font-body-l-accent"
};
const variants = {
  primary: "bg-action-primary hover:bg-active-action-primary active:bg-active-action-primary text-static-key-light",
  secondary: "bg-neutral-secondary hover:bg-active-neutral-secondary active:bg-active-neutral-secondary text-primary",
  danger: "bg-danger-primary hover:bg-active-danger-primary active:bg-active-danger-primary text-static-key-light",
  tertiary: "bg-special-nulled hover:bg-action-secondary active:bg-action-secondary text-action-primary",
  floating: "bg-layer-floor-2 shadow-md hover:shadow active:shadow disabled:shadow-md text-primary",
  "static-light": "bg-static-key-light hover:bg-static-active-key-light active:bg-static-active-key-light text-static-key-dark"
};
const states = {
  disabled: "bg-neutral-secondary hover:bg-neutral-secondary active:bg-neutral-secondary text-tertiary cursor-not-allowed",
  loading: "cursor-progress text-transparent"
};
function mapDisabledStateToStyle(disabled, variant) {
  if (disabled) {
    if (variant === "static-light") {
      return "bg-static-key-light hover:bg-static-key-light active:bg-static-key-light text-static-tertiary-dark cursor-not-allowed";
    }
    return states.disabled;
  }
  return "active:scale-975 active:ease-in-out active:duration-100";
}
const loadingSpinnerColor = {
  primary: "text-static-key-light",
  secondary: "text-primary",
  danger: "text-static-key-light",
  tertiary: "text-action-primary",
  floating: "text-primary",
  "static-light": "text-static-key-dark"
};

/**
 * Extend a KalepIconElement with given props.
 *
 * @returns KalepIconElement
 */
function withProps(icon, props) {
  return /*#__PURE__*/React.cloneElement(icon, props);
}
export const Button = /*#__PURE__*/React.forwardRef(function createButton(_ref, ref) {
  let {
      children,
      size = "md",
      variant = "primary",
      type = "button",
      disabled = false,
      loading = false,
      fullWidth = false,
      startIcon,
      endIcon,
      onClick,
      contentColor,
      overrideClassName
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  // it does not make sense to be disabled and loading at the same time.
  const isLoading = !disabled && loading;
  const buttonContent = /*#__PURE__*/React.createElement(React.Fragment, null, startIcon && withProps(startIcon, {
    size,
    className: "-ms-1"
  }), children, endIcon && withProps(endIcon, {
    size,
    className: "-me-1"
  }));

  // eslint-disable-next-line react-hooks/rules-of-hooks -- forwardRef inner function confuses linter
  const hasSemanticContentColor = React.useMemo(() => colors.find(color => color === contentColor), [contentColor]);

  // eslint-disable-next-line react-hooks/rules-of-hooks -- forwardRef inner function confuses linter
  const style = React.useMemo(() => {
    switch (variant) {
      case "primary":
        {
          return {
            backgroundImage: "var(--color-bg-action-primary)" // allows gradient backgrounds
          };
        }
      case "static-light":
        {
          if (!hasSemanticContentColor && !disabled && contentColor) {
            return {
              color: contentColor
            };
          }
          return {};
        }
      default:
        {
          return {};
        }
    }
  }, [variant, hasSemanticContentColor, contentColor, disabled]);
  return /*#__PURE__*/React.createElement("button", _extends({
    type: type // eslint-disable-line react/button-has-type -- rule would not allow consumers to set type and have a default
    ,
    disabled: disabled,
    onClick: isLoading ? NOOP : onClick,
    className: cx( /* resets */"border-none cursor-pointer", "flex justify-center items-center flex-none relative", "rounded-full ease-linear duration-150", fullWidth ? "w-full" : "w-fit", sizes[size], variants[variant], variant === "static-light" && contentColor && hasSemanticContentColor ? contentColor : null, mapDisabledStateToStyle(disabled, variant), isLoading && states.loading, overrideClassName),
    style: style,
    ref: ref
  }, props), buttonContent, isLoading ? /*#__PURE__*/React.createElement("div", {
    "data-kalep-button-spinner": true,
    className: cx("absolute", loadingSpinnerColor[variant])
  }, /*#__PURE__*/React.createElement(SpinnerIcon, {
    className: cx("animate-spin block"),
    size: size
  })) : null);
});